import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Phone, Clock, CheckCircle } from 'lucide-react';
import { processSteps } from '../mock';

const Process = ({ onBookCall }) => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">The Wine Making Process</h1>
          <p className="text-xl max-w-3xl mx-auto opacity-90">
            Making your own wine is easier than you think. Follow our simple 4-step process and you'll be enjoying your own premium wine in just 4-8 weeks.
          </p>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {processSteps.map((step, index) => (
              <div key={step.id} className="relative">
                <Card className="hover:shadow-xl transition-shadow">
                  <CardContent className="p-8">
                    <div className="flex flex-col md:flex-row items-center gap-8">
                      {/* Step Number & Icon */}
                      <div className="flex-shrink-0 text-center">
                        <div className="bg-[#FDB913] text-gray-900 w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mb-4 mx-auto">
                          {step.id}
                        </div>
                        <img src={step.icon} alt={step.title} className="w-24 h-24 mx-auto" />
                      </div>

                      {/* Content */}
                      <div className="flex-1 text-center md:text-left">
                        <h3 className="text-2xl font-bold text-gray-900 mb-4">
                          Step {step.id}: {step.title}
                        </h3>
                        <p className="text-gray-700 leading-relaxed text-lg">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Connector Line */}
                {index < processSteps.length - 1 && (
                  <div className="flex justify-center my-6">
                    <div className="w-1 h-12 bg-[#FDB913]/30"></div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Additional Details */}
          <div className="mt-16 bg-white rounded-lg p-8 border-2 border-[#FDB913]/20">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">What to Expect</h3>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p className="flex items-start gap-3">
                <Clock className="w-6 h-6 text-[#FDB913] flex-shrink-0 mt-1" />
                <span>
                  <strong>First Visit (5 minutes):</strong> After selecting your wine, we'll help you add the yeast and start the fermentation process. Quick and easy!
                </span>
              </p>
              <p className="flex items-start gap-3">
                <Clock className="w-6 h-6 text-[#FDB913] flex-shrink-0 mt-1" />
                <span>
                  <strong>Fermentation Period (4-8 weeks):</strong> Your wine ages and develops its unique flavors. We'll notify you when it's ready for bottling.
                </span>
              </p>
              <p className="flex items-start gap-3">
                <Clock className="w-6 h-6 text-[#FDB913] flex-shrink-0 mt-1" />
                <span>
                  <strong>Bottling Appointment (30 minutes):</strong> Return to bottle your wine. We'll handle sterilization, filling, labeling, and applying shrink caps.
                </span>
              </p>
            </div>
          </div>

          {/* Benefits */}
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            <Card className="border-[#FDB913]/30 text-center">
              <CardContent className="pt-8 pb-8">
                <CheckCircle className="w-12 h-12 text-[#FDB913] mx-auto mb-4" />
                <h4 className="font-bold text-gray-900 mb-2">Simple & Easy</h4>
                <p className="text-sm text-gray-600">
                  No experience needed. We guide you through every step.
                </p>
              </CardContent>
            </Card>
            <Card className="border-[#FDB913]/30 text-center">
              <CardContent className="pt-8 pb-8">
                <CheckCircle className="w-12 h-12 text-[#FDB913] mx-auto mb-4" />
                <h4 className="font-bold text-gray-900 mb-2">Cost Effective</h4>
                <p className="text-sm text-gray-600">
                  Save money compared to store-bought wine with premium quality.
                </p>
              </CardContent>
            </Card>
            <Card className="border-[#FDB913]/30 text-center">
              <CardContent className="pt-8 pb-8">
                <CheckCircle className="w-12 h-12 text-[#FDB913] mx-auto mb-4" />
                <h4 className="font-bold text-gray-900 mb-2">Fun Experience</h4>
                <p className="text-sm text-gray-600">
                  Enjoy the satisfaction of making your own premium wine.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-lg text-gray-700 mb-8">
            Book a free consultation call and we'll walk you through the entire process, answer your questions, and help you choose the perfect wine.
          </p>
          <Button 
            size="lg" 
            onClick={onBookCall}
            data-track="book_call:process_book_call"
            className="bg-[#FDB913] hover:bg-[#E5A50A] text-gray-900 font-semibold text-lg px-8 py-6"
          >
            <Phone className="w-5 h-5 mr-2" />
            Schedule Your Free Consultation
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Process;